import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Icon, Switch, Toast } from "@/components/ui";
import {
  fetchNotificationPrefs,
  updateNotificationPrefs,
  type NotificationPrefs,
} from "../api";

/** The five categories from the design. v1 has no per-category granularity (see the channel note). */
const CATEGORIES = ["proposals", "deadlines", "messages", "invoices", "statusChanges"] as const;

/**
 * Settings → Notifications (TW-3458). An Email × In-app matrix across the five notification
 * categories, wired to the global channel prefs (GET/PATCH /api/me/notification-prefs) that the
 * fan-out (TW-3435) checks at the source. v1 persists only the two global channel toggles, so
 * every category row binds its two switches to the same two values — toggling one moves them all
 * (flagged by the note). Per-category granularity is a tracked follow-up. Saves instantly per
 * toggle (optimistic, reverts on error).
 */
export function NotificationsSection() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    void fetchNotificationPrefs().then(setPrefs);
  }, []);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  async function toggle(channel: keyof NotificationPrefs, value: boolean) {
    if (!prefs) return;
    const previous = prefs;
    setPrefs({ ...prefs, [channel]: value }); // optimistic
    try {
      const saved = await updateNotificationPrefs({ [channel]: value });
      setPrefs(saved);
      flash(t("settings.notifications.saved"));
    } catch {
      setPrefs(previous); // revert
      flash(t("settings.notifications.saveError"));
    }
  }

  return (
    <div>
      <div className="set-section-head" style={{ marginBottom: 18 }}>
        <h2>{t("settings.notifications.heading")}</h2>
        <div className="sub">{t("settings.notifications.sub")}</div>
      </div>

      <Card>
        <div className="notif-head">
          <div className="nh-spacer" />
          <div className="nh">{t("settings.notifications.email")}</div>
          <div className="nh">{t("settings.notifications.inApp")}</div>
        </div>

        {CATEGORIES.map((key) => (
          <div className="notif-row" key={key}>
            <div className="nr-text">
              <div className="nr-label">{t(`settings.notifications.categories.${key}.label`)}</div>
              <div className="nr-sub">{t(`settings.notifications.categories.${key}.sub`)}</div>
            </div>
            <div className="nr-toggles">
              <div className="nr-cell">
                <Switch
                  checked={prefs?.email ?? false}
                  disabled={!prefs}
                  onChange={(v) => void toggle("email", v)}
                  label={t("settings.notifications.channelLabel", {
                    channel: t("settings.notifications.email"),
                    category: t(`settings.notifications.categories.${key}.label`),
                  })}
                />
              </div>
              <div className="nr-cell">
                <Switch
                  checked={prefs?.inApp ?? false}
                  disabled={!prefs}
                  onChange={(v) => void toggle("inApp", v)}
                  label={t("settings.notifications.channelLabel", {
                    channel: t("settings.notifications.inApp"),
                    category: t(`settings.notifications.categories.${key}.label`),
                  })}
                />
              </div>
            </div>
          </div>
        ))}
      </Card>

      <div className="card-foot" style={{ borderTop: "none", paddingTop: 0, justifyContent: "flex-start" }}>
        <span className="foot-note">
          <Icon name="alert" size={13} />
          {t("settings.notifications.channelNote")}
        </span>
      </div>

      {toast && <Toast message={toast} />}
    </div>
  );
}
